import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import {
  Bookmark,
  Factory,
  Globe,
  ListChecks,
  PhoneCall,
  ShieldAlert,
  Star,
  ThumbsUp,
} from 'lucide-react';

import type { TransformedPointOfSaleReport } from '@ballerine/common';
import type { CustomerFeatures } from '@ballerine/customer-schemas';

import type { PreviousReportsLinks } from '../../../report-shared/components/PreviousReportsButton/PreviousReportsButton';
import {
  EcosystemSection,
  HeaderSection,
  MerchantDetailsSection,
  ReferencesSection,
  RiskAnalysisSection,
  SocialMediaSection,
  TagsSection,
} from '../../sections';
import {
  FdaWarningsSection,
  GoogleBusinessOverviewSection,
  MysteryShopperCallHeaderField,
  MysteryShopperCallSection,
} from '../../sections/ends-analysis';

type EndsAnalysisReportSection = {
  id: string;
  title?: string;
  Icon?: LucideIcon;
  Component: ReactNode;
  excludeFromObserver?: boolean;
  isHidden: boolean;
};

export const useEndsAnalysisReportSections = (
  report: TransformedPointOfSaleReport & {
    monitoringStatus?: string | null;
    changesSummary?: string | null;
    isAlert?: boolean | null;
    comparedToReportId?: string | null;
  },
  googleMapsApiKey?: string,
  FeedbackComponent?: React.ComponentType<{ reportId: string }>,
  _Link?: React.ComponentType<{ to: string; children: React.ReactNode; className?: string }>,
  reportType?: string,
  previousReportsLinks?: PreviousReportsLinks | null,
  customerFeatures?: CustomerFeatures | null,
  _locale?: string,
  _BusinessDetailsSlot?: React.ComponentType,
) => {
  const {
    providedDetails,
    ecosystem,
    socialMedia,
    synthesizerResult,
    matchReason,
    merchantDetails,
    operationalProof,
    structuredTags,
  } = report.data;

  const {
    references,
    riskTypes,
    riskLevel,
    riskAnalysis,
    tags: synthesizerTags,
  } = synthesizerResult ?? {};
  const { facebook, instagram, twitter, linkedin, tiktok } = socialMedia ?? {};

  // ENDS reports carry line-of-business where every report type does — the
  // content_analysis domain column, surfaced as operationalProof.
  const lineOfBusiness = operationalProof?.lineOfBusiness ?? null;

  const tags = structuredTags?.length ? structuredTags : synthesizerTags;

  // Flag-gated (BAL-8283): data may already be imported, but the call renders
  // only for customers with the sub-flag on — in the header as well as in its
  // own section.
  const mysteryShopperCall =
    customerFeatures?.ENDS_ANALYSIS?.isMysteryShopperCallEnabled === true
      ? report.mysteryShopperCall
      : null;

  const sections = useMemo(() => {
    const allSections = [
      {
        id: 'header',
        Icon: Globe,
        excludeFromObserver: true,
        isHidden: false,
        Component: (
          <HeaderSection
            reportId={report.id}
            riskLevel={riskLevel}
            lineOfBusiness={lineOfBusiness}
            riskTypes={riskTypes}
            isMonitoringReport={false}
            FeedbackComponent={FeedbackComponent}
            merchantDetails={merchantDetails}
            googleMapsApiKey={googleMapsApiKey}
            reportType={reportType}
            generalInfoExtra={
              mysteryShopperCall ? (
                <MysteryShopperCallHeaderField callStatus={mysteryShopperCall.callStatus} />
              ) : null
            }
          />
        ),
      },
      {
        id: 'tags',
        isHidden: !tags?.length,
        Icon: ListChecks,
        excludeFromObserver: true,
        Component: <TagsSection tags={tags} />,
      },
      {
        id: 'risk-analysis',
        title: 'Risk Analysis',
        isHidden: riskAnalysis == null,
        Icon: ListChecks,
        Component: (
          <RiskAnalysisSection
            summary={riskAnalysis}
            createdAt={
              typeof report.createdAt === 'string' ? new Date(report.createdAt) : report.createdAt
            }
            ongoingMonitoringSummary={null}
            homepageScreenshotUrl={null}
            previousReportsLinks={previousReportsLinks}
          />
        ),
      },
      {
        id: 'merchant-details',
        title: 'Operating Company Details',
        isHidden: !merchantDetails,
        Icon: Factory,
        Component: (
          <MerchantDetailsSection
            merchantDetails={merchantDetails}
            providedDetails={providedDetails}
            matchReason={matchReason}
            googleMapsApiKey={googleMapsApiKey}
          />
        ),
      },
      {
        id: 'mystery-shopper-call',
        title: 'Mystery Shopper Call',
        isHidden: mysteryShopperCall == null,
        Icon: PhoneCall,
        Component: <MysteryShopperCallSection mysteryShopperCall={mysteryShopperCall} />,
      },
      {
        id: 'google-business-overview',
        title: 'Google Business Overview',
        isHidden: report.googleBusinessOverview == null,
        Icon: Star,
        Component: (
          <GoogleBusinessOverviewSection googleBusinessOverview={report.googleBusinessOverview} />
        ),
      },
      {
        id: 'fda-warnings',
        title: 'FDA Warnings',
        isHidden: report.fdaWarnings == null,
        Icon: ShieldAlert,
        Component: <FdaWarningsSection fdaWarnings={report.fdaWarnings} />,
      },
      {
        id: 'ecosystem',
        title: 'Ecosystem',
        isHidden: ecosystem == null,
        Icon: Globe,
        Component: <EcosystemSection ecosystem={ecosystem} />,
      },
      {
        id: 'social-media',
        title: 'Social Media',
        isHidden: socialMedia == null,
        Icon: ThumbsUp,
        Component: (
          <SocialMediaSection
            facebookPage={facebook}
            instagramPage={instagram}
            twitterPage={twitter}
            linkedinPage={linkedin}
            tiktokPage={tiktok}
          />
        ),
      },
      {
        id: 'references',
        title: 'References',
        isHidden: references == null,
        Icon: Bookmark,
        Component: <ReferencesSection references={references} />,
      },
    ] as EndsAnalysisReportSection[];

    return allSections.filter(section => !section.isHidden);
  }, [
    report.id,
    report.createdAt,
    riskLevel,
    lineOfBusiness,
    riskTypes,
    riskAnalysis,
    tags,
    merchantDetails,
    providedDetails,
    matchReason,
    ecosystem,
    socialMedia,
    facebook,
    instagram,
    twitter,
    linkedin,
    tiktok,
    references,
    report.googleBusinessOverview,
    report.fdaWarnings,
    mysteryShopperCall,
    reportType,
    googleMapsApiKey,
    FeedbackComponent,
    previousReportsLinks,
  ]);

  return { sections };
};
